import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';

import { REALTIME } from '@/config/constants';
import { env } from '@/config/env';

type EventCallback = (...args: unknown[]) => void;

/** Namespace Socket.IO `/realtime` trên Gateway (xem tài liệu WebSocket Realtime). */
export function buildGatewayRealtimeSocketUrl(rawBase: string): string {
  const trimmed = rawBase.replace(/\/$/, '');
  if (/\/realtime$/i.test(trimmed)) return trimmed;
  return `${trimmed}/realtime`;
}

class SocketManager {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;

  connect(token?: string): Socket {
    if (this.socket?.connected) return this.socket;

    const url = buildGatewayRealtimeSocketUrl(env.WS_URL || window.location.origin);

    this.socket = io(url, {
      auth: token ? { token } : undefined,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: REALTIME.RECONNECT_INTERVAL,
      reconnectionAttempts: REALTIME.MAX_RECONNECT_ATTEMPTS,
    });

    this.socket.on('connect', () => {
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_attempt', () => {
      this.reconnectAttempts++;
    });

    return this.socket;
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
  }

  subscribeStation(stationId: string): void {
    const id = stationId.trim();
    if (!id) return;
    this.socket?.emit('subscribe:station', { stationId: id });
  }

  unsubscribeStation(stationId: string): void {
    const id = stationId.trim();
    if (!id) return;
    this.socket?.emit('unsubscribe:station', { stationId: id });
  }

  subscribeBasin(basinId: string): void {
    const id = basinId.trim();
    if (!id) return;
    this.socket?.emit('subscribe:basin', { basinId: id });
  }

  unsubscribeBasin(basinId: string): void {
    const id = basinId.trim();
    if (!id) return;
    this.socket?.emit('unsubscribe:basin', { basinId: id });
  }

  on(event: string, callback: EventCallback): void {
    this.socket?.on(event, callback);
  }

  off(event: string, callback?: EventCallback): void {
    this.socket?.off(event, callback);
  }

  emit(event: string, data?: unknown): void {
    this.socket?.emit(event, data);
  }

  get isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  get instance(): Socket | null {
    return this.socket;
  }
}

export const socketManager = new SocketManager();
